import dayjs from "dayjs";
import db from "../db/database";
import { calculateInvoiceTotalsHelper } from "../utils/invoiceTotals";
import { addMoney, resolveMoneyDecimals, roundMoney, sumMoney } from "../utils/money";
import { getDocumentTypeSign } from "../constants/documentType";
import formaDePagoService from "./formaDePagoService";
import { getAllCajas } from "./cajaService";
import { getAllTurnos } from "./turnoService";
import { getAllClients } from "./clientService";
import { getAperturaActiva } from "./cajaEstadoService";
import { getCouponByNo } from "./couponService";
import { getCampaignByNo } from "./campaignService";
import { formasDePagoNetas, loMasViejoSinEnviar, resumenDeDocumentos } from "./estadoDeLosDocumentos";

const DOCUMENT_TYPES_IN_SCOPE = ["Invoice", "Credit Memo"];

// Calcula el rango de fechas (strings "YYYY-MM-DD") para cada opción del
// selector Hoy/Semana/Mes/Rango personalizado del dashboard.
export const getRangeBounds = (rangeKey, customRange) => {
  const today = dayjs();

  switch (rangeKey) {
    case "week":
      return {
        start: today.startOf("week").format("YYYY-MM-DD"),
        end: today.format("YYYY-MM-DD"),
      };
    case "month":
      return {
        start: today.startOf("month").format("YYYY-MM-DD"),
        end: today.format("YYYY-MM-DD"),
      };
    case "custom":
      return {
        start: (customRange?.[0] ? dayjs(customRange[0]) : today).format("YYYY-MM-DD"),
        end: (customRange?.[1] ? dayjs(customRange[1]) : today).format("YYYY-MM-DD"),
      };
    case "today":
    default:
      return { start: today.format("YYYY-MM-DD"), end: today.format("YYYY-MM-DD") };
  }
};

// Facturas y notas de crédito no-borrador en el rango, usando Posting_Date
// (indexado, ver schemaV11 en db/database.js) como cota de la consulta.
const getInvoicesInRange = async (startDate, endDate) => {
  const endKey = dayjs(endDate).add(1, "day").format("YYYY-MM-DD");

  const invoices = await db.invoices
    .where("Posting_Date")
    .between(startDate, endKey, true, false)
    .toArray();

  return invoices.filter(
    (inv) => !inv.deleted && !inv.isDraft && DOCUMENT_TYPES_IN_SCOPE.includes(inv.Document_Type)
  );
};

// Igual que attachInvoiceTotals (ver InvoiceList.js) pero acotado al set de
// facturas del rango en vez de escanear toda la tabla invoiceLines.
const getLinesForInvoices = async (invoices) => {
  const nos = invoices.map((inv) => inv.No || inv.Pre_Assigned_No).filter(Boolean);
  const ids = invoices.map((inv) => inv.id);

  const [byNo, byId] = await Promise.all([
    nos.length
      ? db.invoiceLines
          .where("invoiceNo")
          .anyOf(nos)
          .and((l) => !l.deleted)
          .toArray()
      : [],
    ids.length
      ? db.invoiceLines
          .where("invoiceId")
          .anyOf(ids)
          .and((l) => !l.deleted)
          .toArray()
      : [],
  ]);

  const linesByNo = new Map();
  byNo.forEach((line) => {
    if (!line.invoiceNo) return;
    if (!linesByNo.has(line.invoiceNo)) linesByNo.set(line.invoiceNo, []);
    linesByNo.get(line.invoiceNo).push(line);
  });

  const linesById = new Map();
  byId.forEach((line) => {
    if (line.invoiceId == null) return;
    if (!linesById.has(line.invoiceId)) linesById.set(line.invoiceId, []);
    linesById.get(line.invoiceId).push(line);
  });

  return invoices.map((invoice) => {
    const key = invoice.No || invoice.Pre_Assigned_No;
    const lines = (key && linesByNo.get(key)) || linesById.get(invoice.id) || [];
    return { invoice, lines };
  });
};

// Adjunta subtotal/IVA/total y el total con signo según el tipo de documento.
//
// **Con sus líneas de pago**, y antes iban vacías. El descuento por forma de
// pago se aplica a partir de ellas, así que sin pasarlas el panel sumaba
// importes sin descontar mientras el detalle de cada factura mostraba el
// correcto. Medido en Ecuador producción: el comprobante electrónico se emite
// por el importe ya descontado --`PVY5R-000004590` timbra 156,35-- y el panel
// contaba 162,87. SANTIECU-10942.
const withTotals = (invoicesWithLines, pagosPorDocumento, country) =>
  invoicesWithLines.map(({ invoice, lines }) => {
    const pagos =
      pagosPorDocumento.get(invoice.id) ??
      pagosPorDocumento.get(invoice.Pre_Assigned_No || invoice.No) ??
      [];
    const { subtotal, vat, total } = calculateInvoiceTotalsHelper(
      { ...invoice, lines },
      pagos,
      country
    );
    const sign = getDocumentTypeSign(invoice.Document_Type, country?.code);
    const absTotal = Math.abs(total);

    return { invoice, lines, subtotal, vat, total: absTotal, signedTotal: sign * absTotal };
  });

/**
 * Calcula las métricas de actividad del dashboard para un rango de fechas.
 * @param {string} startDate "YYYY-MM-DD"
 * @param {string} endDate "YYYY-MM-DD"
 * @param {object} country País activo (para cálculo de totales/IVA)
 */
export const getDashboardMetrics = async (startDate, endDate, country) => {
  const decimals = resolveMoneyDecimals(country);
  const invoicesInRange = await getInvoicesInRange(startDate, endDate);
  const invoicesWithLines = await getLinesForInvoices(invoicesInRange);

  // Las líneas de pago se traen ANTES de calcular los totales, porque el
  // descuento por forma de pago sale de ellas. Se indexan por las dos claves:
  // un pago del terminal se enlaza por `invoiceId`, uno traído de históricos
  // llega con `invoiceId: 0` y sólo trae el número de documento.
  const todosLosIds = invoicesInRange.map((inv) => inv.id).filter((x) => x != null);
  const todosLosPagos = todosLosIds.length
    ? await db.invoicePaymentLines
        .where("invoiceId")
        .anyOf(todosLosIds)
        .and((l) => !l.deleted)
        .toArray()
    : [];
  const numerosDeDocumento = invoicesInRange
    .map((inv) => inv.Pre_Assigned_No || inv.No)
    .filter(Boolean);
  const pagosPorNumero = numerosDeDocumento.length
    ? await db.invoicePaymentLines
        .where("Document_No_")
        .anyOf(numerosDeDocumento)
        .and((l) => !l.deleted)
        .toArray()
    : [];
  const pagosPorDocumento = new Map();
  for (const linea of [...todosLosPagos, ...pagosPorNumero]) {
    for (const clave of [linea.invoiceId, linea.Document_No_]) {
      if (clave === undefined || clave === null || clave === "") continue;
      if (!pagosPorDocumento.has(clave)) pagosPorDocumento.set(clave, []);
      const lista = pagosPorDocumento.get(clave);
      if (!lista.some((l) => l.id === linea.id)) lista.push(linea);
    }
  }

  const enriched = withTotals(invoicesWithLines, pagosPorDocumento, country);

  const invoicesOnly = enriched.filter((e) => e.invoice.Document_Type === "Invoice");
  const creditMemosOnly = enriched.filter((e) => e.invoice.Document_Type === "Credit Memo");

  const ventasNetas = sumMoney(enriched, (e) => e.signedTotal, decimals);
  const vendidoAmount = sumMoney(invoicesOnly, (e) => e.total, decimals);
  const devueltoAmount = sumMoney(creditMemosOnly, (e) => e.total, decimals);
  const ticketPromedio = invoicesOnly.length
    ? roundMoney(vendidoAmount / invoicesOnly.length, decimals)
    : 0;

  // Ventas por forma de pago, NETAS: lo cobrado en facturas menos lo devuelto en
  // notas de crédito, como el arqueo del cierre. Decisión de Solme del 17/09/2026;
  // la cuenta vive en `estadoDeLosDocumentos.js`.
  //
  // Los pagos salen del mismo `pagosPorDocumento` de arriba, que indexa por las
  // DOS claves. Este panel hacía su propia consulta sólo por `invoiceId`, y un
  // pago traído por Traer Históricos llega con `invoiceId: 0` y sólo el número de
  // documento (`syncUtils.js`): decía «Sin ventas registradas en el período» con
  // ventas en el tablero. Medido el 15/09/2026 en CRI DEV.
  const formasDePago = await formaDePagoService.getAll();
  const { ventasPorFormaDePago, cobradoNeto, diferenciaConVentasNetas, causaDeLaDiferencia } =
    formasDePagoNetas({
    documentos: enriched,
    pagosDe: (invoice) =>
      pagosPorDocumento.get(invoice.id) ??
      pagosPorDocumento.get(invoice.Pre_Assigned_No || invoice.No) ??
      [],
    formasDePago,
    ventasNetas,
    decimals,
    countryCode: country?.code,
  });
  const paymentTotal = cobradoNeto;

  // Ventas por caja / turno
  const [cajas, turnos] = await Promise.all([getAllCajas(), getAllTurnos()]);
  const cajaByNo = new Map(cajas.map((c) => [c.No, c]));
  const turnoByNo = new Map(turnos.map((t) => [t.No, t]));
  const cajaGroups = new Map();
  invoicesOnly.forEach((e) => {
    const cajaNo = e.invoice.caja || "-";
    const turnoNo = e.invoice.turno || "-";
    const key = `${cajaNo}||${turnoNo}`;
    if (!cajaGroups.has(key)) {
      cajaGroups.set(key, {
        cajaLabel: cajaByNo.get(cajaNo)?.nombre || cajaNo,
        turnoLabel: turnoByNo.get(turnoNo)?.DescripcionTurno || turnoNo,
        amount: 0,
        count: 0,
      });
    }
    const group = cajaGroups.get(key);
    group.amount = addMoney(group.amount, e.total, decimals);
    group.count += 1;
  });
  const ventasPorCajaTurno = Array.from(cajaGroups.values()).sort((a, b) => b.amount - a.amount);

  // Productos más vendidos (por unidades)
  const productGroups = new Map();
  invoicesOnly.forEach((e) => {
    e.lines.forEach((line) => {
      const key = line.No || line.Description;
      if (!key) return;
      if (!productGroups.has(key)) {
        productGroups.set(key, {
          label: line.Description || line.No,
          quantity: 0,
          amount: 0,
        });
      }
      const group = productGroups.get(key);
      group.quantity += Number(line.Quantity || 0);
      group.amount = addMoney(group.amount, line.Line_Amount || 0, decimals);
    });
  });
  const productosMasVendidos = Array.from(productGroups.values())
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5);

  // Clientes
  const allClients = await getAllClients();
  const clientesTotal = allClients.length;
  const startOfRange = dayjs(startDate).startOf("day");
  const endOfRange = dayjs(endDate).endOf("day");
  const clientesNuevos = allClients.filter((c) => {
    if (!c.createdAt) return false;
    const created = dayjs(c.createdAt);
    return created.isAfter(startOfRange) && created.isBefore(endOfRange);
  }).length;

  // Mejores clientes por monto acumulado (solo facturas)
  const clientByNo = new Map();
  const clientById = new Map();
  allClients.forEach((c) => {
    if (c.No) clientByNo.set(c.No, c);
    clientById.set(c.id, c);
  });
  const clientGroups = new Map();
  invoicesOnly.forEach((e) => {
    const invoice = e.invoice;
    const key = invoice.Sell_to_Customer_No_ ?? invoice.clientLocalId ?? invoice.Bill_to_Name;
    if (!key) return;
    if (!clientGroups.has(key)) {
      const client =
        clientByNo.get(invoice.Sell_to_Customer_No_) || clientById.get(invoice.clientLocalId);
      clientGroups.set(key, {
        label: client?.name || invoice.Bill_to_Name || "Cliente",
        amount: 0,
      });
    }
    clientGroups.get(key).amount = addMoney(clientGroups.get(key).amount, e.total, decimals);
  });
  const mejoresClientes = Array.from(clientGroups.values())
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Uso de cupones y campañas
  const couponGroups = new Map();
  invoicesOnly.forEach((e) => {
    const code = e.invoice.Cupon || e.invoice.Campaign_No_;
    if (!code) return;
    if (!couponGroups.has(code)) {
      couponGroups.set(code, { code, count: 0, amount: 0 });
    }
    const group = couponGroups.get(code);
    group.count += 1;
    group.amount = addMoney(group.amount, e.invoice.importeDto || 0, decimals);
  });

  const cuponesYCampanas = await Promise.all(
    Array.from(couponGroups.values())
      .sort((a, b) => b.amount - a.amount)
      .map(async (group) => {
        // El código puede ser de un cupón o de una campaña: se prueban en ese
        // orden y, si no es ninguno, se muestra el código tal cual. Antes esto
        // eran dos try/catch anidados porque getCouponByNo lanzaba cuando no
        // encontraba nada; ahora devuelve null y el flujo se lee de corrido.
        let label = group.code;

        try {
          const coupon = await getCouponByNo(group.code);

          if (coupon) {
            label = coupon.Description || group.code;
          } else {
            const campaign = await getCampaignByNo(group.code);
            label = campaign?.Description || group.code;
          }
        } catch (error) {
          // Un fallo real de la base local: se deja el código y se sigue, que
          // una etiqueta no vale tumbar el tablero.
          console.error(`No se pudo resolver el nombre del descuento ${group.code}:`, error);
        }

        return { ...group, label };
      })
  );
  const cuponesUsosTotal = cuponesYCampanas.reduce((s, g) => s + g.count, 0);
  const cuponesDescuentoTotal = sumMoney(cuponesYCampanas, (g) => g.amount, decimals);

  // Facturas/notas con error de sincronización en el rango
  const facturasConError = enriched.filter((e) => e.invoice.syncError).length;

  return {
    ventasNetas,
    vendidoAmount,
    devueltoAmount,
    ticketPromedio,
    facturasCount: invoicesOnly.length,
    notasCreditoCount: creditMemosOnly.length,
    ventasPorFormaDePago,
    paymentTotal,
    ventasPorCajaTurno,
    productosMasVendidos,
    clientesTotal,
    clientesNuevos,
    mejoresClientes,
    cuponesYCampanas,
    cuponesUsosTotal,
    cuponesDescuentoTotal,
    facturasConError,
    cobradoNeto,
    diferenciaConVentasNetas,
    causaDeLaDiferencia,
    estadoDeLosDocumentos: resumenDeDocumentos({
      documentos: invoicesInRange,
      countryCode: country?.code,
    }),
    // Sin mirar el período: una venta de hace días sin enviar importa aunque el
    // tablero esté en «Hoy». `pendingSync` está indexado.
    sinEnviar: loMasViejoSinEnviar(
      await db.invoices
        .where("pendingSync")
        .equals(1)
        .and((inv) => !inv.deleted && !inv.isDraft)
        .toArray()
    ),
  };
};

// Última apertura y último cierre registrados localmente, sin importar el
// rango de fechas seleccionado en el dashboard (siempre "lo más reciente").
export const getUltimaAperturaYCierre = async () => {
  let ultimaApertura = await getAperturaActiva();

  if (!ultimaApertura) {
    const aperturas = await db.aperturaDeCaja.filter((a) => !a.deleted).toArray();
    aperturas.sort(
      (a, b) =>
        new Date(`${b.FechaDeApertura}T${b.HoraDeApertura || "00:00"}`) -
        new Date(`${a.FechaDeApertura}T${a.HoraDeApertura || "00:00"}`)
    );
    ultimaApertura = aperturas[0] || null;
  }

  const cierres = await db.cierreDeCaja.filter((c) => !c.deleted).toArray();
  cierres.sort((a, b) => new Date(b.FechaDeCierre || 0) - new Date(a.FechaDeCierre || 0));
  const ultimoCierre = cierres[0] || null;

  let cajaLabel = null;
  if (ultimaApertura?.Caja) {
    const cajas = await getAllCajas();
    cajaLabel = cajas.find((c) => c.No === ultimaApertura.Caja)?.nombre || ultimaApertura.Caja;
  }

  return { ultimaApertura, ultimoCierre, cajaLabel };
};
